import React from 'react'
import { Link } from 'react-router-dom'

export default function Privacy() {
  const Section = ({ title, children }) => (
    <section className="mt-6">
      <h2 className="text-base font-semibold text-gray-900 mb-2">{title}</h2>
      <div className="text-sm text-gray-700 space-y-2 leading-relaxed">{children}</div>
    </section>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-sky-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/azcclogo.jpg" alt="Arizona Carpet Care" className="h-10 w-auto rounded-md shadow-sm" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-xs text-gray-500">Service Schedule Management System</p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            This Privacy Policy (“Policy”) describes how <span className="font-semibold">The Tighlman Group LLC</span> (also referred to as Tighlman Group LLC, “Developer,” “we,” “us,” or “our”) collects, uses, discloses, and otherwise processes information in connection with the Arizona Carpet Care field management scheduler (the “System”). The System is a business-to-business application licensed for use by Arizona Carpet Care (“Client”) and its authorized personnel. It is not a public marketing website.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            The System is available at{' '}
            <a href="https://azccschedule.com" className="text-brand-600 hover:text-brand-700 font-medium">azccschedule.com</a>
            {' '}and{' '}
            <a href="https://arizona-carpet-care.vercel.app" className="text-brand-600 hover:text-brand-700 font-medium">arizona-carpet-care.vercel.app</a>.
            Rules governing use of the System are set out separately in our{' '}
            <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">Terms of Use</Link>, which apply alongside this Policy.
          </p>

          <Section title="1. Scope and Acceptance">
            <p>
              This Policy applies to information processed through the System, including sign-in, scheduling, property and crew records, and related operational features used by authorized Client personnel.
            </p>
            <p>
              By accessing or using the System, you acknowledge that you have read, understood, and agree to this Policy. If you do not agree, you must immediately discontinue use of the System.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>
              We collect only the information needed to operate the System for the Client’s internal scheduling. Categories may include:
            </p>
            <p>
              <span className="font-medium">Account and authentication information.</span> When an authorized user signs in, we process the email address and password credentials associated with that account. Authentication is provided through email and password (via our infrastructure provider). We do not operate SMS marketing, a public chatbot, or other consumer-facing communication channels through the System.
            </p>
            <p>
              <span className="font-medium">Business data entered by authorized managers.</span> Authorized Client personnel may enter and manage job, property, customer, and crew records in the ordinary course of scheduling. That information may include names, addresses, phone numbers, emails, notes, standing directions, billing-method and status flags (including past-due invoice and do-not-service indicators), crew names and related crew settings, job dates and times, comments, and similar operational details.
            </p>
            <p>
              <span className="font-medium">Activity and audit records.</span> The System records activity for security, auditing, troubleshooting, and operational oversight, as further described in Section 4 of the{' '}
              <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">Terms of Use</Link>. Activity logs may include sign-in events, changes to records, descriptions of actions, and timestamps. Soft-deleted records may be retained in the System for operational or audit purposes until the Client instructs otherwise or they are permanently removed.
            </p>
            <p>
              <span className="font-medium">Technical information from hosting and authentication.</span> Our infrastructure providers may automatically log information needed to operate and secure the System, such as IP address, device or browser type, timestamps, and similar connection metadata. We do not add advertising pixels, analytics trackers, or a cookie banner to the System.
            </p>
          </Section>

          <Section title="3. How We Use Information">
            <p>
              We use information to operate the internal scheduler for authorized Client personnel, including to:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>authenticate users and maintain access to the System;</li>
              <li>create, display, update, and print job, property, crew, and related scheduling records;</li>
              <li>support real-time collaboration among authorized users;</li>
              <li>maintain activity logs for security, auditing, troubleshooting, and operational oversight;</li>
              <li>prevent, detect, investigate, and respond to unauthorized access, abuse, or violations of our terms or applicable law;</li>
              <li>comply with legal obligations and Client instructions; and</li>
              <li>establish, exercise, or defend legal rights and claims.</li>
            </ul>
            <p>
              The Tighlman Group LLC and its authorized personnel may access System data and the application as needed to host, maintain, update, secure, troubleshoot, and support the System for the Client. This access continues for as long as we provide hosting or support.
            </p>
          </Section>

          <Section title="4. How We Share Information">
            <p>
              We do not sell personal information. We share information only as described below or as otherwise directed by the Client:
            </p>
            <p>
              <span className="font-medium">Developer access.</span> The Tighlman Group LLC and its authorized personnel may access System data and the application as needed to host, maintain, update, secure, troubleshoot, and support the System for the Client. This access continues for as long as we provide hosting or support.
            </p>
            <p>
              <span className="font-medium">Hosting and infrastructure providers.</span> We use Supabase for authentication and database hosting, and Vercel for application hosting. These providers process information as needed to run the System.
            </p>
            <p>
              <span className="font-medium">Other processors.</span> We may engage additional service providers (for example, email delivery, error monitoring, or professional advisors) solely as needed to operate, secure, or support the System. Those processors may access information only to perform their services.
            </p>
            <p>
              <span className="font-medium">Legal and safety.</span> We may disclose information when we believe in good faith that disclosure is necessary to comply with applicable law, legal process, or a government request, or to protect the rights, property, or safety of The Tighlman Group LLC, the Client, users, or the public.
            </p>
            <p>
              <span className="font-medium">Business transfers.</span> In connection with a merger, acquisition, financing, reorganization, sale of assets, or similar transaction, information may be transferred to a potential or actual successor.
            </p>
          </Section>

          <Section title="5. Security">
            <p>
              Access to the System is limited to authenticated users. Data in transit is protected with HTTPS. We take commercially reasonable measures designed to protect information from loss, misuse, and unauthorized access, disclosure, alteration, or destruction.
            </p>
            <p>
              No method of transmission over the Internet or method of electronic storage is completely secure. We cannot and do not guarantee the absolute security of any information. You transmit and store information in the System at your own risk. Additional warranty and liability terms appear in the{' '}
              <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">Terms of Use</Link>.
            </p>
          </Section>

          <Section title="6. Retention">
            <p>
              We retain information as needed to operate the System, to satisfy legal, accounting, audit, or dispute-resolution requirements, and as otherwise instructed by the Client. Soft-deleted records may remain in the System until they are permanently removed or the Client directs otherwise.
            </p>
            <p>
              We may retain information in backups, archives, or disaster-recovery systems for a period after it is removed from active use. The Client remains responsible for deciding what business records to keep and for maintaining any independent backups it requires.
            </p>
          </Section>

          <Section title="7. Access, Correction, and Deletion">
            <p>
              Authorized users may view and update many records directly in the System, subject to the permissions granted by the Client.
            </p>
            <p>
              To request access to, correction of, or deletion of personal information, or to ask a privacy question, contact us using the email addresses in Section 10. We may need to verify your identity and, where the request concerns Client business data, confirm the request with the Client before fulfilling it. We will respond as required by applicable law.
            </p>
          </Section>

          <Section title="8. Children">
            <p>
              The System is a workplace scheduling tool for authorized Client personnel. It is not directed to children under the age of thirteen (13), and we do not knowingly collect personal information from children under thirteen (13). If you believe a child has provided us with personal information, please contact us and we will take reasonable steps to delete it.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Policy at any time. When we do, we will revise the effective or last-updated date on this page. Continued access to or use of the System after a change takes effect constitutes acceptance of the updated Policy. If you do not agree, you must immediately cease use of the System.
            </p>
          </Section>

          <Section title="10. Contact">
            <p>
              For privacy questions, access, correction, or deletion requests, or notices required under this Policy, contact The Tighlman Group LLC at{' '}
              <a href="mailto:contact@tighlmangroup.com" className="text-brand-600 hover:text-brand-700 font-medium">
                contact@tighlmangroup.com
              </a>
              {' '}or{' '}
              <a href="mailto:zachary.williams@tighlmangroup.com" className="text-brand-600 hover:text-brand-700 font-medium">
                zachary.williams@tighlmangroup.com
              </a>.
            </p>
            <p>
              Use of the System is also governed by our{' '}
              <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">Terms of Use</Link>.
            </p>
          </Section>

          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
            <span>Effective: September 2026 · Last updated: September 2026</span>
            <div className="flex items-center gap-3">
              <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">
                Terms of Use
              </Link>
              <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
                ← Back to sign in
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} Tighlman Group LLC. All rights reserved.{' '}
          <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">Terms</Link>
        </p>
      </div>
    </div>
  )
}
